import React, { useState } from 'react'
import styled from 'styled-components'
import { FiPlay, FiStar } from 'react-icons/fi'
import { 
  Container, 
  Card, 
  Grid, 
  Heading1, 
  Heading2, 
  BodyText, 
  SmallText, 
  Button, 
  StatusBadge,
  LoadingSpinner 
} from '../components/ui'
import { theme, mediaQuery } from '../theme'

const FormGrid = styled(Grid)`
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`

const Label = styled.label`
  font-weight: 600;
  color: ${theme.colors.primary.main};
  font-size: ${theme.typography.body.p2.fontSize};
`

const Input = styled.input`
  padding: ${theme.spacing.sm};
  border: 2px solid ${theme.colors.secondary.beige};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.body.p2.fontSize};
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
  }
  
  &[type="number"] {
    max-width: 100px;
  }
`

const CurrentValuesCard = styled(Card)`
  background: ${theme.colors.secondary.beige};
  margin-bottom: ${theme.spacing.lg};
`

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};
`

const ValueItem = styled.div`
  text-align: center;
  padding: ${theme.spacing.sm};
  background: white;
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${theme.colors.primary.light};
`

const ValueLabel = styled.div`
  font-size: ${theme.typography.body.p3.fontSize};
  color: ${theme.colors.primary.light};
  margin-bottom: ${theme.spacing.xs};
`

const ValueScore = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${theme.colors.primary.main};
`

const RecommendationCard = styled(Card)`
  margin-bottom: ${theme.spacing.md};
  border-left: 4px solid ${theme.colors.secondary.green};
`

const RecommendationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.sm};
  gap: ${theme.spacing.md};
`

const RecommendationTitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${theme.colors.primary.main};
  flex: 1;
`

const ScoreBadge = styled.div`
  background: ${theme.colors.secondary.green};
  color: white;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-weight: 600;
  font-size: ${theme.typography.body.p3.fontSize};
  white-space: nowrap;
`

const RecommendationExplanation = styled.div`
  color: ${theme.colors.primary.light};
  margin-bottom: ${theme.spacing.sm};
  line-height: 1.5;
`

const NoRecommendations = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.primary.light};
`

const PlaysPage = () => {
  const [userId, setUserId] = useState('')
  const [journalEntry, setJournalEntry] = useState({
    pressure: '',
    hours: '',
    focus: '',
    calm: '',
    happiness: '',
    depression: '',
    mania: '',
    energy: ''
  })
  const [recommendations, setRecommendations] = useState([])
  const [analysisContext, setAnalysisContext] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const emotionalFactorLabels = {
    hours: 'Personal Identity',
    focus: 'Focus',
    calm: 'Calm',
    happiness: 'Positivity',
    depression: 'Satisfaction',
    mania: 'Control',
    energy: 'Energy'
  }

  const handleInputChange = (field, value) => {
    const numValue = value === '' ? '' : parseFloat(value)
    if (value === '' || (numValue >= 0 && numValue <= 10)) {
      setJournalEntry(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  const handleGetRecommendations = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Validate that we have pressure and at least one emotional factor
      if (journalEntry.pressure === '') {
        setError('Pressure score is required')
        return
      }

      const hasEmotionalFactor = Object.keys(emotionalFactorLabels).some(
        factor => journalEntry[factor] !== ''
      )

      if (!hasEmotionalFactor) {
        setError('At least one emotional factor is required')
        return
      }

      // Convert string values to numbers for API call
      const entryForAPI = {}
      Object.keys(journalEntry).forEach(key => {
        if (journalEntry[key] !== '') {
          entryForAPI[key] = parseFloat(journalEntry[key])
        }
      })

      const response = await fetch('/api/analytics/plays/recommendations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          journalEntry: entryForAPI,
          userId: userId || 'anonymous'
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get recommendations')
      }

      setRecommendations(data.data.recommendations)
      setAnalysisContext(data.data.analysisContext)

    } catch (err) {
      setError(err.message)
      console.error('Failed to get recommendations:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const getDisplayValues = () => {
    const values = {}
    Object.keys(emotionalFactorLabels).forEach(factor => {
      if (journalEntry[factor] !== '') {
        values[factor] = parseFloat(journalEntry[factor])
      }
    })
    return values
  }

  const formatScore = (score) => {
    return (score * 100).toFixed(1)
  }

  return (
    <Container>
      <div style={{ marginBottom: theme.spacing.xl }}>
        <Heading1>Plays Recommendations</Heading1>
        <BodyText>
          Enter a journal entry and get personalized activity recommendations
        </BodyText>
      </div>

      {/* User ID Input */}
      <Card style={{ marginBottom: theme.spacing.lg }}>
        <Heading2>User Information</Heading2>
        <InputGroup>
          <Label>User ID (optional)</Label>
          <Input
            type="text"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="Enter user ID for personalized recommendations"
          />
        </InputGroup>
      </Card>

      {/* Journal Entry Form */}
      <Card style={{ marginBottom: theme.spacing.lg }}>
        <Heading2>Journal Entry</Heading2>
        <BodyText style={{ marginBottom: theme.spacing.md }}>
          Enter a pressure score (0-10) and at least one emotional factor (0-10)
        </BodyText>

        <FormGrid>
          <InputGroup>
            <Label>Pressure Score *</Label>
            <Input
              type="number"
              min="0"
              max="10"
              step="0.1"
              value={journalEntry.pressure}
              onChange={(e) => handleInputChange('pressure', e.target.value)}
              placeholder="0-10"
            />
          </InputGroup>

          {Object.entries(emotionalFactorLabels).map(([factor, label]) => (
            <InputGroup key={factor}>
              <Label>{label}</Label>
              <Input
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={journalEntry[factor]}
                onChange={(e) => handleInputChange(factor, e.target.value)}
                placeholder="0-10"
              />
            </InputGroup>
          ))}
        </FormGrid>

        <Button 
          onClick={handleGetRecommendations} 
          disabled={isLoading || journalEntry.pressure === ''}
          fullWidth
        >
          <FiPlay style={{ marginRight: '8px' }} />
          {isLoading ? 'Getting Recommendations...' : 'Get Play Recommendations'}
        </Button>

        {error && (
          <Card style={{ 
            marginTop: theme.spacing.md, 
            background: theme.colors.status.error, 
            color: 'white' 
          }}>
            <BodyText style={{ color: 'white', margin: 0 }}>
              {error}
            </BodyText>
          </Card>
        )}
      </Card>

      {/* Current Values Display */}
      {(journalEntry.pressure !== '' || Object.values(getDisplayValues()).length > 0) && (
        <CurrentValuesCard>
          <Heading2>Current Entry Values</Heading2>
          <ValuesGrid>
            {journalEntry.pressure !== '' && (
              <ValueItem>
                <ValueLabel>Pressure</ValueLabel>
                <ValueScore>{journalEntry.pressure}</ValueScore>
              </ValueItem>
            )}
            {Object.entries(getDisplayValues()).map(([factor, value]) => (
              <ValueItem key={factor}>
                <ValueLabel>{emotionalFactorLabels[factor]}</ValueLabel>
                <ValueScore>{value}</ValueScore>
              </ValueItem>
            ))}
          </ValuesGrid>
          {analysisContext && (
            <div style={{ marginTop: theme.spacing.md }}>
              <SmallText>
                Average Emotional Score: {analysisContext.avgEmotionalScore} | 
                Factors needing improvement: {analysisContext.factorsNeedingImprovement.join(', ') || 'None'}
              </SmallText>
            </div>
          )}
        </CurrentValuesCard>
      )}

      {/* Loading State */}
      {isLoading && (
        <Card style={{ textAlign: 'center' }}>
          <LoadingSpinner />
          <BodyText>Analyzing your entry and finding the best plays...</BodyText>
        </Card>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div>
          <Heading2 style={{ marginBottom: theme.spacing.lg }}>
            Recommended Plays for You
          </Heading2>
          
          {recommendations.map((rec, index) => (
            <RecommendationCard key={rec.activityId}>
              <RecommendationHeader>
                <RecommendationTitle>
                  {index + 1}. {rec.text}
                </RecommendationTitle>
                <ScoreBadge>
                  {formatScore(rec.score)}% match
                </ScoreBadge>
              </RecommendationHeader>
              
              <RecommendationExplanation>
                {rec.explanation}
              </RecommendationExplanation>
              
              <SmallText>
                Activity ID: {rec.activityId}
                {rec.dataQuality?.usedPersonalizedData && rec.dataQuality?.usedGeneralData && (
                  <span> | Based on personalized + general data</span>
                )}
                {rec.dataQuality?.usedPersonalizedData && !rec.dataQuality?.usedGeneralData && (
                  <span> | Based on personalized data</span>
                )}
                {!rec.dataQuality?.usedPersonalizedData && rec.dataQuality?.usedGeneralData && (
                  <span> | Based on general data</span>
                )}
              </SmallText>
            </RecommendationCard>
          ))}

          {analysisContext && (
            <Card style={{ background: theme.colors.secondary.beige, marginTop: theme.spacing.lg }}>
              <Heading2>Analysis Summary</Heading2>
              <Grid columns="repeat(auto-fit, minmax(200px, 1fr))">
                <div>
                  <SmallText>Activities Analyzed</SmallText>
                  <BodyText style={{ fontWeight: 600 }}>
                    {analysisContext.activitiesAnalyzed}
                  </BodyText>
                </div>
                <div>
                  <SmallText>Reliable Activity Data</SmallText>
                  <BodyText style={{ fontWeight: 600 }}>
                    {analysisContext.activitiesWithReliableData}
                  </BodyText>
                </div>
                <div>
                  <SmallText>Impact Data Records</SmallText>
                  <BodyText style={{ fontWeight: 600 }}>
                    {analysisContext.impactDataAvailable}
                  </BodyText>
                </div>
                <div>
                  <SmallText>Reliable Impact Data</SmallText>
                  <BodyText style={{ fontWeight: 600 }}>
                    {analysisContext.impactsWithReliableData}
                  </BodyText>
                </div>
                <div>
                  <SmallText>Pressure Level</SmallText>
                  <BodyText style={{ fontWeight: 600 }}>
                    {analysisContext.pressureLevel}/10
                  </BodyText>
                </div>
                <div>
                  <SmallText>Factors Provided</SmallText>
                  <BodyText style={{ fontWeight: 600 }}>
                    {analysisContext.totalFactorsProvided}/7
                  </BodyText>
                </div>
              </Grid>
            </Card>
          )}
        </div>
      )}

      {/* No Recommendations State */}
      {!isLoading && recommendations.length === 0 && analysisContext && (
        <Card>
          <NoRecommendations>
            <FiStar size={48} style={{ color: theme.colors.primary.light, marginBottom: theme.spacing.md }} />
            <BodyText>
              No recommendations available. This might be because the activities and impacts tables are empty or don't contain data for your emotional factors.
            </BodyText>
            <SmallText style={{ marginTop: theme.spacing.md }}>
              Activities analyzed: {analysisContext.activitiesAnalyzed} | 
              Impact records: {analysisContext.impactDataAvailable}
            </SmallText>
          </NoRecommendations>
        </Card>
      )}
    </Container>
  )
}

export default PlaysPage 